import React from "react";
import styles from "./VoyageurFond.module.css";
import {resoudre} from "../reglages";
import {REGLAGES} from "./reglages";
import {COUCHES, ambianceSelonHeure, dureeCouche, dureeCycle} from "./defilement";
import {CADRE, CADRE_FEUILLE, HAUTEUR, IMAGES_PAR_CYCLE, LARGEUR_PLAN, SOL, imagesAmbiance} from "./peinture";

/*
 * Fond « Voyageur » : un voyageur à l'écharpe rouge court (ou marche) devant un paysage à défilement parallèle
 * (ciel, montagnes, collines, deux forêts, sol, herbes de premier plan). Plus un plan est loin, moins il bouge.
 * Adapté de l'artéfact « Aerith Parallax » de l'utilisateur (dessin et code originaux). L'artéfact peint tout à chaque image
 * dans un canvas ; ici chaque plan est peint UNE fois (peinture.js, gardé en mémoire par ambiance) puis glisse par un seul
 * déplacement CSS (transform), sans minuteur : même rendu, presque aucun travail pour le poste.
 * Réglages (reglages.js) : ambiance, vitesse du sol, allure, voyageur affiché ou non, finesse du pixel.
 */

export default function VoyageurFond({reglages, images: galerie = [], horaires}) {
    // feuille du voyageur choisie dans la galerie (outil Sprites : cases de 64 × 48, ligne 1 marcher, ligne 2 courir) ; si elle ne se
    // charge pas, on retombe sur le voyageur d'origine
    const feuille = galerie[0];
    // nombre d'images de chaque ligne = largeur / 32, connue une fois la feuille chargée ({adresse, nombre} ou {adresse, hs: true})
    const [lue, setLue] = React.useState(null);
    const {ambiance, vitesse, allure, voyageur, detail, cadence, hauteur, taille, cadrage} = resoudre(REGLAGES, reglages);
    const etat = feuille && lue && lue.adresse === feuille ? lue : null;
    const feuilleUtilisee = etat && !etat.hs ? feuille : null;
    const cadre = feuilleUtilisee ? CADRE_FEUILLE : CADRE;
    const nombre = feuilleUtilisee ? etat.nombre : IMAGES_PAR_CYCLE;
    // « automatique » se recalcule à chaque affichage du bandeau (une fois par seconde) : le fond change d'ambiance à l'heure dite
    const nom = ambiance === "auto" ? ambianceSelonHeure(new Date(), horaires) : ambiance;
    const images = imagesAmbiance(nom);
    const e = detail === "epais" ? 2 : 1;   // pixels d'écran par pixel d'art

    const plan = (id, periode, url, duree) => (
        <div
            key={id}
            className={styles.plan}
            style={{
                width: `calc(100% + ${periode * e}px)`,
                height: HAUTEUR * e,
                backgroundImage: `url(${url})`,
                backgroundSize: `${periode * e}px ${HAUTEUR * e}px`,
                "--deplacement": `${-periode * e}px`,
                animationDuration: duree ? `${duree}s` : undefined,
                animationName: duree ? undefined : "none",
            }}
        />
    );

    // pixels d'écran par pixel d'une case : la feuille personnalisée a ses propres cases (64 × 48), mises à l'échelle par le seul réglage de taille (indépendant de la finesse du pixel)
    const k = feuilleUtilisee ? taille : e;
    // la ligne des pieds de la case se pose sur la ligne du sol ; `hauteur` (pixels d'art) monte ou descend le voyageur
    const bas = (feuilleUtilisee ? (HAUTEUR - SOL) * e - (cadre.hauteur - cadre.pieds) * k : (HAUTEUR - 90 - CADRE.hauteur) * e) + hauteur * e;
    const astre = images.astre;
    return (
        <div className={styles.scene} aria-hidden="true">
            <div className={styles.monde} style={{transform: `translateY(${cadrage * e}px)`}}>
                <div className={styles.ciel} style={{height: HAUTEUR * e, backgroundImage: `url(${images.ciel})`, backgroundSize: `${LARGEUR_PLAN * e}px ${HAUTEUR * e}px`}}/>
                {images.etoiles && (
                    <div className={`${styles.ciel} ${styles.etoiles}`} style={{height: HAUTEUR * e, backgroundImage: `url(${images.etoiles})`, backgroundSize: `${LARGEUR_PLAN * e}px ${HAUTEUR * e}px`}}/>
                )}
                <img
                    className={styles.astre}
                    src={astre.url}
                    alt=""
                    style={{width: astre.cote * e, height: astre.cote * e, bottom: (HAUTEUR - astre.y - astre.cote) * e}}
                />

                {COUCHES.filter(couche => couche.id !== "herbes").map(couche => plan(couche.id, LARGEUR_PLAN, images.plans[couche.id], dureeCouche(couche, vitesse)))}

                {voyageur && (
                    <div
                        className={styles.voyageur}
                        style={{width: cadre.largeur * k, height: cadre.hauteur * k, marginLeft: -cadre.centre * k, bottom: bas}}
                    >
                        <div
                            className={styles.pas}
                            style={{
                                width: cadre.largeur * nombre * k,
                                height: cadre.hauteur * k,
                                backgroundImage: `url(${feuilleUtilisee || images.voyageur[allure]})`,
                                backgroundSize: `${cadre.largeur * nombre * k}px ${cadre.hauteur * (feuilleUtilisee ? 2 : 1) * k}px`,
                                backgroundPosition: feuilleUtilisee && allure === "courir" ? `0 ${-cadre.hauteur * k}px` : undefined,
                                "--deplacement": `${-cadre.largeur * nombre * k}px`,
                                animationDuration: `${feuilleUtilisee ? nombre / cadence : dureeCycle(allure, vitesse)}s`,
                                animationTimingFunction: `steps(${nombre})`,
                            }}
                        />
                    </div>
                )}
                {feuille && (!etat || feuilleUtilisee) && (
                    <img
                        src={feuille}
                        alt=""
                        style={{display: "none"}}
                        onLoad={ev => setLue({adresse: feuille, nombre: Math.max(1, Math.round(ev.target.naturalWidth / CADRE_FEUILLE.largeur))})}
                        onError={() => setLue({adresse: feuille, hs: true})}
                    />
                )}
                {/* les herbes de premier plan passent devant le voyageur : elles sont dessinées après lui */}
                {plan("herbes-devant", LARGEUR_PLAN / 2, images.plans.herbes, dureeCouche(COUCHES[COUCHES.length - 1], vitesse))}
            </div>
        </div>
    );
}
